/*
 https://resources.arshades.it/docs/user-guides/shopify-plugin/customization/manual-integration
 * ARShades manual integration — Steps 4, 6 and 7 of the ARShades guide.
 *
 * Step 5 (the guide's own modal system) is deliberately NOT implemented. Launching goes through
 * the theme's modal system instead: the `vto-modal` element (assets/vto-modal.js) extending
 * `ModalDialog`, which already owns focus trapping, Escape/overlay close, scroll lock and
 * releasing the iframe on close. We hand it the URL on `data-url` and call `show(button)`.
 *
 * URLs are built per click so the currently selected variant is always honoured.
 *   VTO: {webDomain}?c={catalogueId}&m={catalogueVariantId}&layout=v1&lang={lang}&sin=1
 *   3D:  {studioDomain}/Arshades3d/{viewerId}/glasses/{catalogueProductId}/variant/{catalogueVariantId}
 */
if (!customElements.get('ar-shades-buttons')) {
  customElements.define(
    'ar-shades-buttons',
    class ARShadesButtons extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;

        this.querySelectorAll('[data-ars-action]').forEach((button) => {
          button.addEventListener('click', () => {
            if (button.dataset.arsAction === 'vto') {
              this.openVto(button);
            } else {
              this.open3d(button);
            }
          });
        });
      }

      /* --- Step 4: Shopify analytics bridge --- */

      // Forwards events to the ARShades Pixel extension. Fails silently when analytics is absent.
      safePublish(eventName, payload) {
        try {
          if (window.Shopify?.analytics?.publish) {
            window.Shopify.analytics.publish(eventName, payload);
            return true;
          }
        } catch (error) {
          console.warn('[ARShades] publish error', error);
        }
        return false;
      }

      /* --- Step 4: variant detection --- */

      // Prefers the product form's variant input, then ?variant=, then the Liquid-rendered fallback.
      getActiveVariantId(fallback) {
        const scope = this.closest('product-info') || document;
        const input = scope.querySelector('input[name="id"], select[name="id"]');
        if (input?.value) return String(input.value);

        const fromUrl = new URLSearchParams(window.location.search).get('variant');
        if (fromUrl) return String(fromUrl);

        return String(fallback || '');
      }

      readData(attribute) {
        const selector = this.getAttribute(attribute);
        const node = selector && document.querySelector(selector);
        if (!node) return null;
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          console.warn('[ARShades] could not parse', selector, error);
          return null;
        }
      }

      // Falls back to the first mapped variant when the selected one has no ARShades model,
      // matching the guide's behaviour.
      resolveVariant(data) {
        const selectedId = this.getActiveVariantId(data.currentVariantId);
        const match = data.variants[selectedId];
        if (match) return { selectedId, match };

        const keys = Object.keys(data.variants);
        return { selectedId, match: keys.length ? data.variants[keys[0]] : null };
      }

      // Hands the built URL to the theme's modal rather than the guide's own overlay.
      openInThemeModal(button, url) {
        const modal = document.querySelector(this.dataset.modal);
        if (!modal) return;
        button.setAttribute('data-url', url);
        modal.show(button);
      }

      /* --- Step 6: VTO button handler --- */

      openVto(button) {
        const data = this.readData('data-vto-data');
        if (!data) return;

        const { selectedId, match: catalogueVariantId } = this.resolveVariant(data);
        if (!catalogueVariantId) return;

        const url =
          data.baseUrl +
          '?c=' +
          encodeURIComponent(data.catalogueId) +
          '&m=' +
          encodeURIComponent(catalogueVariantId) +
          '&layout=v1' +
          '&lang=' +
          encodeURIComponent(data.language || 'en') +
          '&sin=1' +
          '&arsBridge=1';

        const productId = data.shopifyProductId ? String(data.shopifyProductId) : null;
        this.safePublish('arshades:vto_started', {
          shopifyVariantId: selectedId,
          shopifyProductId: productId,
          catalogueId: data.catalogueId,
          catalogueVariantId: catalogueVariantId
        });
        this.safePublish('ars_vto_started', {
          variantId: selectedId,
          productId: productId,
          catalogueId: data.catalogueId
        });

        this.openInThemeModal(button, url);
      }

      /* --- Step 7: 3D viewer button handler --- */

      open3d(button) {
        const data = this.readData('data-3d-data');
        if (!data) return;

        const { selectedId, match } = this.resolveVariant(data);
        if (!match) return;

        const url =
          data.baseUrl + '/Arshades3d/' + data.viewerId + '/glasses/' + match.productId + '/variant/' + match.variantId;

        this.safePublish('arshades:3d_opened', {
          shopifyVariantId: selectedId,
          shopifyProductId: data.shopifyProductId ? String(data.shopifyProductId) : null,
          catalogueId: data.catalogueId,
          viewerId: data.viewerId,
          catalogueVariantId: match.variantId,
          catalogueProductId: match.productId
        });

        this.openInThemeModal(button, url);
      }
    }
  );
}
